import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
} from '@mui/icons-material';
import { leaveAdminAPI, LeaveReasonItem } from '../services/api';

const SYSTEM_REQUEST_TYPES = [
  { code: 'ferie', label: 'Ferie' },
  { code: 'permesso', label: 'Permesso' },
  { code: 'ex_festivita', label: 'Ex Festività' },
  { code: 'malattia', label: 'Malattia' },
  { code: 'altri', label: 'Altri' },
];

const LeaveReasonsAdmin: React.FC = () => {
  const [reasons, setReasons] = useState<LeaveReasonItem[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const loadReasons = async () => {
    setLoading(true);
    try {
      const data = await leaveAdminAPI.getReasons();
      setReasons(data.reasons || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento delle causali');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReasons();
  }, []);

  const handleCreate = async () => {
    if (!newLabel.trim()) {
      setError('Inserisci il nome della nuova causale');
      return;
    }
    setError(null);
    try {
      await leaveAdminAPI.createReason(newLabel.trim());
      setNewLabel('');
      setSuccess('Causale creata');
      await loadReasons();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nella creazione');
    }
  };

  const handleSaveEdit = async (id: number) => {
    if (!editLabel.trim()) {
      setError('Il nome non può essere vuoto');
      return;
    }
    setError(null);
    try {
      await leaveAdminAPI.updateReason(id, { label: editLabel.trim() });
      setEditId(null);
      setSuccess('Causale aggiornata');
      await loadReasons();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'aggiornamento');
    }
  };

  const handleToggleActive = async (item: LeaveReasonItem) => {
    setError(null);
    try {
      if (item.is_active) {
        await leaveAdminAPI.deleteReason(item.id);
        setSuccess('Causale nascosta dalle nuove richieste');
      } else {
        await leaveAdminAPI.updateReason(item.id, { is_active: true });
        setSuccess('Causale riattivata');
      }
      await loadReasons();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'aggiornamento');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Voci ferie e permessi
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        I tipi di richiesta di sistema restano fissi. Puoi aggiungere, rinominare o nascondere
        le causali dei permessi (Studio, ROL, Lutto, …) senza intervento tecnico.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tipi richiesta
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {SYSTEM_REQUEST_TYPES.map((item) => (
              <Chip key={item.code} label={item.label} color="primary" variant="outlined" />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Causali permesso
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              label="Nuova causale"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreate();
                }
              }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              Aggiungi
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Causale</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reasons.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {editId === item.id ? (
                        <TextField
                          size="small"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          onBlur={() => handleSaveEdit(item.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveEdit(item.id);
                            }
                          }}
                          autoFocus
                          fullWidth
                        />
                      ) : (
                        <Typography
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            setEditId(item.id);
                            setEditLabel(item.label);
                          }}
                        >
                          {item.label}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.is_active ? 'Attiva' : 'Nascosta'}
                        color={item.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(item)}
                        title={item.is_active ? 'Nascondi' : 'Riattiva'}
                      >
                        {item.is_active ? <HideIcon /> : <ShowIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && reasons.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>Nessuna causale configurata</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LeaveReasonsAdmin;
